import {
    LocationType, AddressMainCategory,
    DeliveryServiceType,
    HandoverTime,
} from 'app/enums/label.enums'
import {
    FormPart,
    NestedField,
} from 'app/types/form.types'
import {
    Lane,
    InvoiceSettingsType,
} from 'app/Apps/OrderManagement/Lanes/lanes.types'
import proformaInvoiceFields from 'app/Apps/OrderManagement/Lanes/LaneImplementation/fields/proformaInvoice.fields'

const getFields = ({
    getAllowedValues,
    reqCustomsCoord,
    isDropOffSkyCell,
    deliveryServiceType,
}:{
    getAllowedValues: (enumType: Record<string, string>) => Record<string, string>,
    reqCustomsCoord: boolean,
    isDropOffSkyCell: boolean,
    deliveryServiceType: DeliveryServiceType,
}): FormPart<keyof (Lane & InvoiceSettingsType)>[] => {
    const isNotDropOffSkyCellFields: NestedField<keyof Lane>[] = [
        {
            name: 'fwdExport',
            componentName: 'FreightForwarderSelector',
            labelKey: 'LaneOpsInformationDto.fwdExport',
        },
        {
            name: 'fwdExportContacts',
            componentName: 'MultipleContactObjectSelector',
            labelKey: 'LaneOpsInformationDto.fwdExportContacts',
        },
        {
            name: 'fwdExportBackup',
            componentName: 'FreightForwarderSelector',
            labelKey: 'LaneOpsInformationDto.fwdExportBackup',
        },
        {
            name: 'fwdExportBackupContacts',
            componentName: 'MultipleContactObjectSelector',
            labelKey: 'LaneOpsInformationDto.fwdExportBackupContacts',
        },
    ]

    return [
        {
            title: 'Lane Information',
            className: 'laneInformation',
            fields: [
                {
                    id: 'laneInformation1',
                    fields: [
                        {
                            name: 'laneNumber',
                            componentName: 'InputSingleline',
                            disabled: true,
                            labelKey: 'OrderLaneView.laneNumber',
                        },
                        {
                            name: 'customerExecutionContactsOps',
                            componentName: 'MultipleContactObjectSelector',
                            required: true,
                            labelKey: 'LaneOpsInformationDto.customerExecutionContactsOps',
                        },
                    ],
                },
                {
                    id: 'laneInformation3',
                    fields: [
                        {
                            name: 'handoverPointContacts',
                            componentName: 'MultipleContactObjectSelector',
                            labelKey: 'LaneOpsInformationDto.handoverPointContacts',
                            required: true,
                        },
                        {
                            name: 'endOfLeaseReminderDelay',
                            componentName: 'IntegerInput',
                            labelKey: 'LaneOpsInformationDto.endOfLeaseReminderDelay',
                        },
                    ],
                },
                {
                    id: 'laneInformation4',
                    fields: [
                        {
                            name: 'collectionDropoffPointContacts',
                            componentName: 'MultipleContactObjectSelector',
                            labelKey: 'LaneOpsInformationDto.collectionDropoffPointContacts',
                            required: true,
                        },
                        {
                            name: 'laneCommentOps',
                            componentName: 'LongText',
                            labelKey: 'LaneOpsInformationDto.laneCommentOps',
                        },
                    ],
                },
            ],
        },
        proformaInvoiceFields(getAllowedValues),
        {
            title: 'Positioning',
            className: 'positioning',
            fields: [
                {
                    id: 'positioning1',
                    fields: [
                        {
                            name: 'handoverEarliest',
                            componentName: 'EnumSelector',
                            allowedValues: getAllowedValues(HandoverTime),
                            labelKey: 'LaneOpsInformationDto.handoverEarliest',
                        },
                        {
                            name: 'handoverLatest',
                            componentName: 'EnumSelector',
                            allowedValues: getAllowedValues(HandoverTime),
                            labelKey: 'LaneOpsInformationDto.handoverLatest',
                        },
                        {
                            name: 'containerSelectionMax',
                            componentName: 'IntegerInput',
                            labelKey: 'LaneOpsInformationDto.containerSelectionMax',
                        },
                        {
                            name: 'orderInstructions',
                            componentName: 'LongText',
                            labelKey: 'LaneOpsInformationDto.orderInstructions',
                        },
                        ...deliveryServiceType === DeliveryServiceType.PICKUP ? []
                            : [
                                {
                                    name: 'fwdPos',
                                    componentName: 'FreightForwarderSelector',
                                    labelKey: 'LaneOpsInformationDto.fwdPos',
                                },
                                {
                                    name: 'fwdPosContacts',
                                    componentName: 'MultipleContactObjectSelector',
                                    labelKey: 'LaneOpsInformationDto.fwdPosContacts',
                                },
                            ] as NestedField<keyof Lane>[],
                    ],
                },
                {
                    id: 'positioning2',
                    fields: [
                        {
                            name: 'originSc',
                            componentName: 'LocationObjectSelector',
                            filterAddressMainCategory: AddressMainCategory.SERVICECENTER,
                            filterLocationType: LocationType.ADDRESS,
                            labelKey: 'LaneOpsInformationDto.originSc',
                        },
                        {
                            name: 'originScContacts',
                            componentName: 'MultipleContactObjectSelector',
                            labelKey: 'LaneOpsInformationDto.originScContacts',
                        },
                        {
                            name: 'originScBackup',
                            componentName: 'LocationObjectSelector',
                            filterAddressMainCategory: AddressMainCategory.SERVICECENTER,
                            filterLocationType: LocationType.ADDRESS,
                            labelKey: 'LaneOpsInformationDto.originScBackup',
                        },
                        {
                            name: 'originScBackupContacts',
                            componentName: 'MultipleContactObjectSelector',
                            labelKey: 'LaneOpsInformationDto.originScBackupContacts',
                        },
                        ...deliveryServiceType === DeliveryServiceType.PICKUP ? []
                            : [
                                {
                                    name: 'fwdPosBackup',
                                    componentName: 'FreightForwarderSelector',
                                    labelKey: 'LaneOpsInformationDto.fwdPosBackup',
                                },
                                {
                                    name: 'fwdPosBackupContacts',
                                    componentName: 'MultipleContactObjectSelector',
                                    labelKey: 'LaneOpsInformationDto.fwdPosBackupContacts',
                                },
                            ] as NestedField<keyof Lane>[],
                    ],
                },
            ],
        },
        {
            title: 'Reverse Logistics',
            className: 'reverseLogistics',
            fields: [
                {
                    id: 'reverseLogistics1',
                    fields: [
                        {
                            name: 'reqCustomsCoord',
                            componentName: 'Checkbox',
                            labelKey: 'LaneOpsInformationDto.reqCustomsCoord',
                        },
                        {
                            name: 'fwdCustoms',
                            componentName: 'FreightForwarderSelector',
                            required: reqCustomsCoord,
                            labelKey: 'LaneOpsInformationDto.fwdCustoms',
                        },
                        {
                            name: 'fwdCustomsContacts',
                            componentName: 'MultipleContactObjectSelector',
                            required: reqCustomsCoord,
                            labelKey: 'LaneOpsInformationDto.fwdCustomsContacts',
                        },
                    ],
                },
                {
                    id: 'reverseLogistics2',
                    fields: isDropOffSkyCell ? [] : isNotDropOffSkyCellFields,
                },
            ],
        },
    ]
}

export default getFields
